import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, TriangleAlert, Zap } from 'lucide-react';

import { canSee, ROLES, useSession } from '@/features/auth';
import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton, useToastStore } from '@/shared/ui';

import type { CampaignRollup, CampaignSurtSource } from '../model/types';
import {
  useCampaign,
  useCloseCampaign,
  useLaunchCampaign,
  usePreview,
  useReconcileCampaign,
} from '../queries/useCampaigns';
import styles from './Campaigns.module.css';
import { CampaignState } from './CampaignsScreen';

/**
 * Kampania: postęp, zalegający, akcje (spec 7A).
 *
 * ⭐ **Ekran uzgadnia zbiór przy wejściu** (decyzja 86). Skoro ktoś patrzy, to chce świeżego
 * stanu — a dynamika polega właśnie na tym, że element powstały wczoraj sam wpada do zbioru.
 * Przebieg w tle robi to samo bez człowieka; ten wyzwalacz istnieje po to, żeby patrzący nie
 * czekał do najbliższego przebiegu.
 */
export function CampaignScreen() {
  const { t } = useTranslation();
  const params = useParams();
  const describeError = useApiErrorMessage();
  const { data: user } = useSession();
  const show = useToastStore((s) => s.show);

  const id = Number(params['id']);
  const campaign = useCampaign(id);
  const reconcile = useReconcileCampaign();
  const launch = useLaunchCampaign();
  const close = useCloseCampaign();

  const mayWrite = canSee(user, [ROLES.architect]);
  const status = campaign.data?.campaign.status;

  // ⚠ Martwe źródło surta = uzgodnienie skończy się dokładnie tym samym komunikatem przy każdym
  // wejściu (decyzja 89: wsad odmawia). Zamiast wołać i pokazywać błąd — nie wołamy, a o stanie
  // mówi baner przy źródle. To jest powód, dla którego `CampaignSurtSource` w ogóle jedzie
  // w detalu, a nie tylko identyfikator.
  const sourceDead = campaign.data?.surtSource?.obsolete === true;

  // ⚠ Uzgadniamy RAZ na wejście, nie przy każdym renderze. Bez tej pamięci każde odświeżenie
  // danych (a te unieważniają się po zamknięciu zadania gdziekolwiek) wywoływałoby zapis.
  const reconciled = useRef<number | null>(null);

  useEffect(() => {
    if (status !== 'launched' || sourceDead || reconciled.current === id) {
      return;
    }

    reconciled.current = id;

    reconcile.mutate(id, {
      onSuccess: (result) => {
        // ⭐ Mówimy TYLKO wtedy, gdy coś doszło. „Uzgodniono, nic nowego" przy każdym wejściu
        // byłoby szumem — i to szumem, który uczy ignorować powiadomienia.
        if (result.created > 0) {
          show({ message: t('campaigns.reconciled', { count: result.created }), tone: 'success' });
        }
      },
    });
    // ⭐ Zależności są PEŁNE, a przed powtórzeniem broni referencja wyżej — nie tablica.
    // To rozróżnienie jest tu istotne: tablica zależności mówi „przelicz, gdy to się zmieni",
    // a my potrzebujemy „zrób dokładnie raz na tę kampanię". Wyciszenie reguły ukryłoby, że
    // strażnikiem jest co innego, niż się wydaje.
  }, [id, status, sourceDead, reconcile, show, t]);

  if (campaign.isPending) {
    return (
      <div className={styles['loading']} aria-busy="true">
        <Skeleton width="24rem" height="2rem" announce />
        <Skeleton height="6rem" />
      </div>
    );
  }

  if (campaign.isError) {
    return (
      <div className={styles['failure']} role="alert">
        <p>{describeError(campaign.error)}</p>
        <Button onClick={() => void campaign.refetch()}>{t('common.action.retry')}</Button>
      </div>
    );
  }

  const { campaign: summary, rollup, surtSource } = campaign.data;

  return (
    <div className={styles['screen']}>
      <header className={styles['head']}>
        <h1 className={styles['title']}>{summary.title}</h1>
        <CampaignState status={summary.status} />
        <Link className={styles['link']} to="/campaigns">
          <ArrowLeft size={15} aria-hidden="true" />
          {t('campaigns.backToList')}
        </Link>
      </header>

      <p className={styles['hint']}>
        {t('campaigns.task', { title: summary.template.title })}
        {summary.template.activation === 'iterationNext'
          ? ` · ${t('campaigns.taskIterationNext')}`
          : ''}
      </p>

      {surtSource ? <SurtSourceLine source={surtSource} /> : null}

      {mayWrite ? (
        <div className={styles['actions']}>
          {summary.status === 'draft' ? (
            <>
              <PreviewLine campaignId={id} />
              <Button
                variant="primary"
                disabled={launch.isPending}
                onClick={() =>
                  launch.mutate(id, {
                    onSuccess: () =>
                      show({ message: t('campaigns.launched'), tone: 'success' }),
                    onError: (error) =>
                      show({ message: describeError(error), tone: 'danger' }),
                  })
                }
              >
                {t('campaigns.action.launch')}
              </Button>
            </>
          ) : null}

          {summary.status === 'launched' ? (
            <Button
              disabled={close.isPending}
              onClick={() =>
                close.mutate(id, {
                  onSuccess: () => show({ message: t('campaigns.closed'), tone: 'neutral' }),
                  onError: (error) => show({ message: describeError(error), tone: 'danger' }),
                })
              }
            >
              {t('campaigns.action.close')}
            </Button>
          ) : null}

          {/* ⚠ Zdanie o skutku zamknięcia stoi PRZY przycisku, a nie w dokumentacji:
              „zatrzymaj" bywa czytane jako „odwołaj", a zadania zostają u ludzi. */}
          {summary.status === 'launched' ? (
            <span className={styles['muted']}>{t('campaigns.action.closeHint')}</span>
          ) : null}
        </div>
      ) : null}

      {rollup ? <Rollup rollup={rollup} /> : null}
      {rollup && rollup.laggards.length > 0 ? <Laggards rollup={rollup} /> : null}
    </div>
  );
}

/**
 * Na co czeka kampania w trybie „surt" — i czy to jeszcze możliwe.
 *
 * ⚠ Martwe źródło to jedyny stan kampanii wymagający decyzji człowieka, dlatego dostaje
 * baner z `role="alert"`, a nie szare zdanie: rollup pokazuje wtedy prawdziwe liczby,
 * które bez tego zdania są mylące (zbiór już nie urośnie).
 */
function SurtSourceLine({ source }: { source: CampaignSurtSource }) {
  const { t } = useTranslation();

  const link = (
    <Link
      className={styles['link']}
      to={`/constellation/${source.hostElementId}?panel=commitments&el=${source.hostElementId}`}
    >
      {source.title}
    </Link>
  );

  if (source.obsolete) {
    return (
      <p className={styles['sourceDead']} role="alert">
        <TriangleAlert size={15} aria-hidden="true" />
        {t('campaigns.surt.obsolete')} {link} · {source.hostElementTitle}
      </p>
    );
  }

  return (
    <p className={styles['muted']}>
      {source.done ? t('campaigns.surt.done') : t('campaigns.surt.waiting')} {link} ·{' '}
      {source.hostElementTitle}
    </p>
  );
}

/**
 * Postęp liczbami.
 *
 * ⭐ **„Nieaktualne" stoi osobno od „zrobione"** i to nie jest kosmetyka: zlanie ich w jedno
 * „zamknięte" ukryłoby dokładnie tę informację, po którą jest druga oś statusu — ilu ludzi
 * uznało, że kampania ich nie dotyczy. To jest sygnał o kampanii, nie o nich.
 */
function Rollup({ rollup }: { rollup: CampaignRollup }) {
  const { t } = useTranslation();

  const metrics = [
    { key: 'total', value: rollup.total, danger: false },
    { key: 'done', value: rollup.done, danger: false },
    { key: 'open', value: rollup.open, danger: false },
    { key: 'obsolete', value: rollup.obsolete, danger: false },
    { key: 'overdue', value: rollup.overdue, danger: rollup.overdue > 0 },
  ];

  return (
    <div className={styles['rollup']}>
      {metrics.map((metric) => (
        <div
          key={metric.key}
          className={`${styles['metric']} ${metric.danger ? styles['metricDanger'] : ''}`}
        >
          <span className={styles['metricValue']}>{metric.value}</span>
          <span className={styles['metricLabel']}>{t(`campaigns.rollup.${metric.key}`)}</span>
        </div>
      ))}
    </div>
  );
}

/** Kto zalega — z nazwiskiem właściciela, bo bez tego lista mówi „coś jest niezrobione". */
function Laggards({ rollup }: { rollup: CampaignRollup }) {
  const { t } = useTranslation();

  return (
    <section className={styles['section']}>
      <h2 className={styles['sectionTitle']}>{t('campaigns.laggards.heading')}</h2>

      <div className={styles['wrap']}>
        <table className={styles['table']}>
          <thead>
            <tr>
              <th scope="col">{t('campaigns.laggards.element')}</th>
              <th scope="col">{t('campaigns.laggards.owners')}</th>
            </tr>
          </thead>
          <tbody>
            {rollup.laggards.map((laggard) => (
              <tr key={laggard.commitmentId}>
                <td>
                  {/* Prowadzi na element z OTWARTYM panelem zobowiązań — pulpit i listy robią
                      tak samo: przenosimy tam, gdzie da się coś zrobić, nie tylko obejrzeć. */}
                  <Link
                    className={styles['link']}
                    to={`/constellation/${laggard.elementId}?panel=commitments&el=${laggard.elementId}`}
                  >
                    {laggard.elementTitle}
                  </Link>
                </td>
                <td className={styles['muted']}>
                  {laggard.ownerNames.length > 0
                    ? laggard.ownerNames.join(', ')
                    : t('campaigns.laggards.noOwner')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * ⚡ „Ile trafi" przed uruchomieniem.
 *
 * ⭐ Ten sam mechanizm, co podgląd przy iteracji: **decyzja o wsypaniu roboty setkom ludzi nie
 * zapada w ciemno**. Osobno ostrzegamy o selektorze bez warunków — kampania na całą organizację
 * ma być decyzją, a nie skutkiem pustego formularza.
 */
function PreviewLine({ campaignId }: { campaignId: number }) {
  const { t } = useTranslation();
  const preview = usePreview(campaignId, true);

  if (preview.isPending || preview.isError) {
    return null;
  }

  return (
    <span className={styles['iconLine']}>
      <Zap size={15} aria-hidden="true" />
      {t('campaigns.preview', { count: preview.data.matchedCount })}

      {/* Ostrzeżenie o pustym warunku ma WŁASNĄ ikonę, bo mówi coś innego niż podgląd:
          pierwsza część liczy, druga uprzedza. Wcześniej ⚠ siedziało w tłumaczeniu. */}
      {preview.data.selectorIsEmpty ? (
        <>
          <span aria-hidden="true">·</span>
          <TriangleAlert className={styles['warn']} size={15} aria-hidden="true" />
          {t('campaigns.previewEverything')}
        </>
      ) : null}
    </span>
  );
}
